"use strict";
// Tenant subscription entitlements — the single source of truth for which
// feature-domains each tier unlocks. Shared between the Angular app, Cloud
// Functions, and the seed/provisioning scripts so the tier→feature mapping
// can never drift between layers (GEN-004).
//
// A tier is a property of the CUSTOMER (= tenant), orthogonal to a user's role
// (admin/editor/viewer/timetracking). Effective access is `role AND feature`:
// the role grants the permission, the entitlement grants the capability.
//
// This module is intentionally platform-agnostic: no Firebase/Firestore imports
// (GEN-004). Consumers read/write the resolved `features` map; this file only
// defines the catalog and the pure resolver.
Object.defineProperty(exports, "__esModule", { value: true });
exports.TRIAL_DAYS = exports.TRIAL_STATUSES = exports.TENANT_LIMIT_KEYS = exports.SEAT_ALLOWANCES = exports.TIERS = exports.REGISTRATION_SOURCES = exports.REGISTRATION_SELECTABLE_TIER_IDS = exports.SELECTABLE_TIER_IDS = exports.TIER_IDS = exports.FEATURE_KEYS = exports.APP_FEATURE_KEYS = exports.TIER_FEATURE_KEYS = void 0;
exports.isSelectableTierId = isSelectableTierId;
exports.isRegistrationSelectableTierId = isRegistrationSelectableTierId;
exports.isRegistrationSource = isRegistrationSource;
exports.resolveLimits = resolveLimits;
exports.effectiveLimits = effectiveLimits;
exports.resolveLimitsPatch = resolveLimitsPatch;
exports.normalizeTenantLimits = normalizeTenantLimits;
exports.storageUsage = storageUsage;
exports.storageRemainingBytes = storageRemainingBytes;
exports.isWithinLimit = isWithinLimit;
exports.isTierId = isTierId;
exports.isFeatureKey = isFeatureKey;
exports.isTrialStatus = isTrialStatus;
exports.effectiveFeatures = effectiveFeatures;
exports.isFreeOriginTrial = isFreeOriginTrial;
exports.trialStatus = trialStatus;
exports.computeTrial = computeTrial;
exports.canAssignTimetrackingRole = canAssignTimetrackingRole;
exports.canHoldMobileSeats = canHoldMobileSeats;
exports.timetrackingChangeBlockers = timetrackingChangeBlockers;
exports.resolveFeatures = resolveFeatures;
exports.diffFeatureOverrides = diffFeatureOverrides;
const app_catalog_js_1 = require("./app-catalog.js");
const storage_package_js_1 = require("./storage-package.js");
/**
 * The gated capability keys — one per plan-matrix row (PRD-1807, ARCH-002 Tier
 * catalog amendment). EVERY key is fully wired at both enforcement layers (a
 * `hasFeature()` rules clause + a `featureGuard(<key>)` route guard; `aiChat`
 * and `ocr` additionally callable-side), including keys that are currently
 * green for every defined tier — the model and the enforcement never drift.
 * Only genuine infrastructure (settings, users, support, onboarding, home)
 * stays ungated.
 *
 * `expenses` is forward-looking: its module is not built yet, the key is
 * inert until it exists — no public surface may sell it. (`esignature` shipped
 * with the Angebotsportal, PRD-1810, and gates the quote portal alone; contract
 * signing reads `contracts` — ARCH-002 PRD-1206 constraint 8.)
 *
 * `documentDispatch` (Belegversand, PRD-1807 Amendment 1) gates an ACTION — the
 * quote/invoice e-mail send — not a route: it is wired callable-side
 * (`sendDocumentEmail`) plus a `hasFeature()` read clause on the send-history,
 * and the in-page send action shows an upsell when it is absent. It separates
 * *creating* a document (`quotes`/`invoicing`) from *delivering* it, so `free`
 * can create Angebote but not send them.
 *
 * `timetracking` is BOTH a user role (ARCH-001) and a capability: effective
 * access is `role AND feature`, and the role is hard-coupled to the
 * capability (ARCH-001, timetracking-is-role-AND-feature amendment).
 */
exports.TIER_FEATURE_KEYS = [
    'crm',
    'sales',
    'partners',
    'incomingInvoices',
    'invoicing',
    'expenses',
    'quotes',
    'esignature',
    'projects',
    'timetracking',
    'calendar',
    'import',
    'ocr',
    'documentDispatch',
    'workflows',
    'recurringInvoices',
    'orderDocuments',
    'dunning',
    // PRD-1298 (#1298): a second sales pipeline. A capability, not a count limit,
    // because the security rules cannot count a collection (ARCH-002 § 2,
    // DATA-030). Free and Standard keep the one built-in pipeline, freely
    // editable under `sales`; Business gets it through `ALL_TIER_ON`.
    'multiplePipelines',
    // PRD-1657 (#1657): Abschlags- and Schlussrechnungen. Gates creating and
    // issuing both kinds (DATA-037) — never reading, paying, reversing or
    // correcting one that exists. Standard and Business; Business gets it
    // through `ALL_TIER_ON`.
    'partialInvoices',
];
/**
 * The capability keys a tier NEVER grants by default — they are acquired by
 * booking the app that carries them (PRD-816, `APP_CATALOG`).
 *
 * This set exists because of how `business` is built. It is `{ ...ALL_ON }`
 * generated from the key list, so **every key added to the list would be `true`
 * for `business` by default** — the moment a future domain's keys arrived, every
 * business tenant would own the whole domain for free and the store would have
 * nothing left to sell to the customers most able to buy it. `aiChat` escaped
 * that only because someone set it to `false` by hand, which is a convention no
 * test protected.
 *
 * Listing a key here is therefore the deliberate act of putting it on the app
 * axis instead of the tier ladder, and the spec's partition + no-tier-grants-it
 * invariants are what stop a new app from being given away by omission.
 */
exports.APP_FEATURE_KEYS = [
    'aiChat',
    'externalApi',
    'taxAdvisorHandover',
    'taxAdvisorAccess',
    // PRD-854: stock management. On the app axis rather than the ladder because
    // only a minority of tenants handle physical goods at all — a consultant
    // billing retainers must never see a stock column, whatever they pay.
    'inventory',
    // PRD-1845: purchasing — the second half of the Warenwirtschaft, sold under the
    // same bundle as `inventory`. The key is RECLAIMED from the tier ladder, where
    // it gated an empty project-detail placeholder for a tendering flow that was
    // never built; it now means one thing, and that thing is the
    // Einkauf. A `business` tenant loses that empty page and nothing else.
    'procurement',
    // PRD-1017/PRD-1412: the KI-Agent — the agent surface that prepares quotes and
    // invoices (ARCH-008 amendments "Agent surfaces" and "An agent surface serves
    // tasks"). On the app axis because it spends on an inference provider and is
    // sold as its own app with its own allowance entry.
    'aiAgent',
    // PRD-1375: KI-Reporting — an AI-written management report. On the app axis
    // because every report spends on an inference provider; sold as its own app
    // with its own metered path (BE-006).
    'aiReporting',
    // PRD-1632: Team-Chat — direct messages and groups between tenant members. On
    // the app axis because it is sold as its own app, with a base price and a
    // surcharge per user, and is part of no tier.
    'teamChat',
    // PRD-1206: Verträge — contract templates, documents, signing and the
    // overview. On the app axis because it is sold as its own app, flat per
    // workspace with an included monthly allowance of signing requests; it
    // gates writes only, reads stay on crm / partners (ARCH-002 PRD-1206).
    'contracts',
    // PRD-2133: the first connected app (ARCH-013). Its data lives in the connected
    // service, so the key has no rules family: a route guard and `requireFeature`
    // in the connected-app actions are its whole wiring (ARCH-002).
    'leistungsverzeichnis',
];
exports.FEATURE_KEYS = [...exports.TIER_FEATURE_KEYS, ...exports.APP_FEATURE_KEYS];
/** The subscription tiers, cheapest to richest. `free` exists but is not
 * self-service change-selectable (see `SELECTABLE_TIER_IDS`, ARCH-002). */
exports.TIER_IDS = ['free', 'standard', 'business'];
/**
 * The tiers a customer may choose themselves — at signup (`registerTenant`)
 * and via the owner tier change (`setOwnTenantTier`). `free` is assigned
 * exclusively through platform tooling until it opens up for self-service
 * downgrade (ARCH-002, Tier catalog amendment). UI grey-out and server
 * validation both derive from this constant.
 */
exports.SELECTABLE_TIER_IDS = ['standard', 'business'];
/** Type guard: is `value` a tier the customer may select self-service? */
function isSelectableTierId(value) {
    return typeof value === 'string' && exports.SELECTABLE_TIER_IDS.includes(value);
}
/**
 * The tiers a customer may choose **at registration** (`registerTenant`). This
 * is a separate axis from the change-selectable set above (PRD-1807 Amendment 1):
 * `free` is bookable at signup — the acquisition funnel — but NOT a
 * self-service downgrade target on the subscription page (avoids stripping a
 * paid tenant's capabilities). The register page grey-out and `registerTenant`
 * validation both derive from this constant.
 */
exports.REGISTRATION_SELECTABLE_TIER_IDS = ['free', 'standard', 'business'];
/** Type guard: is `value` a tier the customer may select at registration? */
function isRegistrationSelectableTierId(value) {
    return (typeof value === 'string' &&
        exports.REGISTRATION_SELECTABLE_TIER_IDS.includes(value));
}
/**
 * The channels a registration may name as where it came from (ARCH-002
 * amendment *Registration attribution*, PRD-1123). A closed allowlist: public
 * pages link to `/register?source=<value>`, `registerTenant` keeps an
 * allowlisted value as `acquisitionSource` and drops any other one without
 * refusing the registration. Counted by IN-016; nothing branches on it.
 */
exports.REGISTRATION_SOURCES = ['einvoice-tool', 'website'];
/** Type guard: is `value` an allowlisted registration source? */
function isRegistrationSource(value) {
    return typeof value === 'string' && exports.REGISTRATION_SOURCES.includes(value);
}
const ALL_OFF = Object.fromEntries(exports.FEATURE_KEYS.map((key) => [key, false]));
/**
 * Every TIER capability on. Built from `TIER_FEATURE_KEYS`, not from the full
 * key list — that is the whole point of the split: the richest tier grants
 * everything the ladder sells and nothing the store does.
 */
const ALL_TIER_ON = { ...ALL_OFF, ...Object.fromEntries(exports.TIER_FEATURE_KEYS.map((key) => [key, true])) };
/**
 * Default feature set per tier — the plan matrix (PRD-1807), exactly. Each
 * richer tier is a strict superset of the one below it, but that is a
 * convention — the resolver does not assume it.
 *
 * - free:       crm + sales + quotes + invoicing + workflows (manage customers,
 *               run a small pipeline, create Angebote, issue a few e-invoices a
 *               month, run guided workflows — but no documentDispatch, so nothing
 *               is delivered from our sender domain; PRD-1807 Amendment 1 / PRD-1798 /
 *               issue #1122)
 * - standard:   crm, sales, invoicing, quotes, import, documentDispatch, workflows,
 *               orderDocuments, partialInvoices
 * - business:   every TIER capability (the top tier — PRD-1807 Amendment 4)
 *
 * `business` is `ALL_TIER_ON` and nothing else: it grants everything the ladder
 * sells and nothing the store does. App capabilities are absent from all three
 * columns by construction (`APP_FEATURE_KEYS`), so a tenant acquires them by
 * booking the app that carries them (PRD-816) rather than by climbing the ladder.
 *
 * `aiChat` was the first key to move onto that axis. The assistant was withdrawn
 * from the tier ladder because its running cost does not fit a flat monthly fee:
 * one turn costs roughly $0.035 at Mistral list prices (the curated-catalog
 * system prompt alone is ~6k input tokens, re-sent on every tool roundtrip), so
 * a handful of daily users exhausts the business fee on inference alone. It
 * returns as a separately bookable app with its own price, priced against that
 * cost rather than absorbed by the tier (issue #816) — and its budget scales
 * with what is paid for it rather than with the tier (BE-006).
 *
 * The capability itself never stopped being a first-class one — key, guards,
 * rules clause and backend gate all remain wired, which is why the store needed
 * no new enforcement layer. A per-tenant `FeatureOverrides` entry still grants
 * it outside the store, for a pilot or a support case.
 *
 * `recurringInvoices` (PRD-733) is **business only**, and deliberately so: it is
 * the one capability that issues and delivers real documents while nobody is
 * watching, so it belongs with the tier whose tenants have the volume to want
 * it and the relationship to be trusted with it.
 *
 * `dunning` (Mahnwesen, PRD-547) is **business only** (issue #1361): the website
 * sells the multi-stage dunning ladder as a Business feature. Standard and free
 * still issue and track invoices; they cannot create reminders, pause or
 * escalate a ladder, or configure it. Existing dunning state on those tenants
 * stays readable and inert — there was no grandfathering.
 *
 * `invoicing` is on `free` because the purchase trigger is the e-invoice mandate,
 * not the address book (issue #1122): a tenant that signs up for an XRechnung
 * must be able to issue one. What keeps it an entry rather than the product is
 * `TIER_LIMITS.free` — a monthly allowance of issued invoices and a line-item
 * cap — and the absent `documentDispatch`: a free tenant downloads its PDF and
 * XRechnung, it never sends them through the shared sender domain, where a
 * forged invoice would cost every tenant its deliverability (BE-013).
 *
 * `sales` is on `free` so a prospect evaluating us as a CRM finds a sales
 * process, not only an address book (issue #1296). `TIER_LIMITS.free.maxOpenDeals`
 * keeps it an entry: a handful of deals in flight at once, where closing one
 * frees its slot.
 *
 * `workflows` (ARCH-004, PRD-1798) is a feature-domain like `crm`: available on
 * EVERY tier (the guided workflow engine is core), but a proper gated capability
 * (a `hasFeature('workflows')` rules clause on `workflow_runs` + a
 * `featureGuard('workflows')` on the hub/wizard routes). Premium capabilities the
 * workflow uses — e-signature, documentDispatch — degrade tier-internally.
 */
exports.TIERS = {
    free: { ...ALL_OFF, crm: true, sales: true, quotes: true, invoicing: true, workflows: true },
    standard: {
        ...ALL_OFF,
        crm: true,
        sales: true,
        invoicing: true,
        quotes: true,
        import: true,
        documentDispatch: true,
        workflows: true,
        // PRD-853: the Auftragskette completes the paid document chain. Deliberately
        // NOT in `free`: that tier issues invoices (issue #1122) but holds no
        // `documentDispatch`, so it could not dispatch the confirmation the chain
        // is built around.
        orderDocuments: true,
        // PRD-1657: Abschlags- and Schlussrechnungen. Not in `free`, by product
        // decision — a tenant that loses the capability keeps full use of the
        // partial and final invoices it already issued (DATA-037).
        partialInvoices: true,
    },
    business: { ...ALL_TIER_ON },
};
/**
 * Included seat allowances per tier (full/mobile): free 1/0, standard 1/0,
 * business 2/10 (issue #1170 re-cut; previously free 1/0, standard 2/0,
 * business 3/10 per PRD-1807 Amendment 5, issue #1078). A tier includes mobile
 * seats only if its features let them be occupied ({@link
 * canHoldMobileSeats}): standard holds neither `timetracking` nor `projects`,
 * so it carries no mobile allowance. A negotiated deal outside these
 * allowances is represented by `billingMode: 'manual'` on the tenant
 * (ARCH-006), a tier-independent flag the seat gate treats as "never blocks"
 * — not by a tier-level allowance.
 */
exports.SEAT_ALLOWANCES = {
    free: { full: 1, mobile: 0 },
    standard: { full: 1, mobile: 0 },
    business: { full: 2, mobile: 10 },
};
const TIER_LIMITS = {
    free: {
        storageGb: 1,
        maxPersons: 4,
        maxCustomers: 5,
        maxQuoteLineItems: 2,
        // One user — the owner. Matches SEAT_ALLOWANCES.free (issue #1143).
        maxMembers: 1,
        maxIssuedInvoicesPerMonth: 3,
        maxInvoiceLineItems: 5,
        maxOpenDeals: 3,
        maxTags: 10,
        maxCustomFieldsPerEntity: 3,
    },
    standard: { storageGb: 10, maxTags: 50, maxCustomFieldsPerEntity: 10 },
    business: { storageGb: 50, maxCustomFieldsPerEntity: 30 },
};
/** The resource limits a tier INCLUDES (fresh object — defaults never mutate):
 * what a plan contains, for the plan cards and the tier catalogue. Never what a
 * limits write stores — that is {@link effectiveLimits} (ARCH-006 PRD-1678
 * amendment point 5). */
function resolveLimits(tier) {
    return { ...TIER_LIMITS[tier] };
}
/**
 * The limits a tenant actually holds: its tier's, with the storage quota raised
 * by the storage package it holds (PRD-1678). This is the figure every limits
 * writer stores and every enforcement layer reads.
 *
 * The package is a REQUIRED argument, `undefined` for none: a writer that could
 * leave it out would write the tier's quota and silently take back storage the
 * tenant pays for. Free sells no package, so one stated for it adds nothing.
 */
function effectiveLimits(tier, storagePackage) {
    const limits = resolveLimits(tier);
    if (tier === 'free' || limits.storageGb === undefined)
        return limits;
    return { ...limits, storageGb: limits.storageGb + (0, storage_package_js_1.storagePackageGb)(storagePackage) };
}
/**
 * Every `TenantLimits` key — the closed set a limits write must cover, and the
 * set a reader keeps when it normalises a stored `limits` map. Exported so that
 * reader never holds its own copy: a copy that misses a new cap reads it as
 * "unlimited", which is the one failure no test on the writing side can see.
 */
exports.TENANT_LIMIT_KEYS = [
    'storageGb',
    'maxPersons',
    'maxCustomers',
    'maxQuoteLineItems',
    'maxMembers',
    'maxIssuedInvoicesPerMonth',
    'maxInvoiceLineItems',
    'maxOpenDeals',
    'maxTags',
    'maxCustomFieldsPerEntity',
];
/**
 * The effective limits (tier + storage package) as a MERGE-SAFE write patch: every `TenantLimits` key is
 * present — the tier's value where it defines one, the injected `absent`
 * sentinel (`FieldValue.delete()` for Firestore writers) where it does not.
 *
 * Exists because paid tiers express "unlimited" by key ABSENCE (see
 * `TIER_LIMITS`), while every tier-change writer persists with
 * `set(..., { merge: true })` — and Firestore merges nested maps per key, so an
 * absent key deletes nothing. Writing `resolveLimits(tier)` through a merge
 * therefore left a tenant upgraded from `free` with its count caps
 * (`maxCustomers`, …) intact forever. The sentinel is injected rather than
 * imported so this module stays platform-agnostic (GEN-004).
 */
function resolveLimitsPatch(tier, storagePackage, absent) {
    const limits = effectiveLimits(tier, storagePackage);
    return Object.fromEntries(exports.TENANT_LIMIT_KEYS.map((key) => [key, limits[key] ?? absent]));
}
/**
 * Coerce a stored `limits` value into a `TenantLimits`, keeping only known
 * finite numeric caps. An absent or garbage value yields `{}` — every cap
 * undefined, i.e. unlimited, the safe default for a pre-limits profile. Every
 * reader of the persisted map goes through here, so a cap the matrix adds is
 * read by all of them the moment it is listed in `TENANT_LIMIT_KEYS`.
 */
function normalizeTenantLimits(raw) {
    const out = {};
    if (raw && typeof raw === 'object') {
        const record = raw;
        for (const key of exports.TENANT_LIMIT_KEYS) {
            const value = record[key];
            if (typeof value === 'number' && Number.isFinite(value))
                out[key] = value;
        }
    }
    return out;
}
/** One gibibyte — quotas are expressed in GiB, matching Cloud Storage billing. */
const BYTES_PER_GB = 1024 ** 3;
/**
 * The consumed share of a tier's storage quota — the one place bytes meet the
 * quota, so the reconciliation's drift report and the plan page's gauge can
 * never disagree (GEN-004).
 *
 * `percent` is deliberately left uncapped: a tenant past its quota should read
 * "120 %", not a silently clamped "100 %". Clamping is the gauge's business, not
 * the arithmetic's. A tier without a quota yields `null` rather than infinity,
 * so a consumer renders "no limit" instead of a bogus number. The quota is
 * reported, not enforced — no upload is blocked on it (issue #664).
 */
function storageUsage(usedBytes, storageGb) {
    const used = Number.isFinite(usedBytes) && usedBytes > 0 ? usedBytes : 0;
    if (storageGb === undefined) {
        return { usedBytes: used, quotaBytes: null, percent: null, exceeded: false };
    }
    const quotaBytes = storageGb * BYTES_PER_GB;
    return {
        usedBytes: used,
        quotaBytes,
        percent: (used / quotaBytes) * 100,
        exceeded: used > quotaBytes,
    };
}
/**
 * What a tenant may still upload under its tier's storage quota (issue #1452),
 * or `null` for no limit. Mirrors the Storage rules' `withinStorageQuota`, so
 * the client pre-check and the API refuse exactly what the rules would: a tier
 * without a quota, or a tenant the nightly reconciliation has not measured
 * yet, is not limited.
 */
function storageRemainingBytes(usedBytes, storageGb) {
    if (usedBytes === null || storageGb === undefined)
        return null;
    return Math.max(0, storageGb * BYTES_PER_GB - usedBytes);
}
/**
 * The single ceiling predicate every enforcement layer shares: may a resource
 * hold `resultingCount` items under `limit`? An ABSENT limit means unlimited
 * (paid tiers), so this returns `true`. Otherwise the resulting total must not
 * exceed the cap — callers adding one item pass `currentCount + 1`, callers
 * validating a whole array pass its length. Keeping the comparison here (rather
 * than inlined per call site) guarantees client, rules-mirroring, and callable
 * enforcement can never disagree on the boundary (GEN-004).
 */
function isWithinLimit(resultingCount, limit) {
    return limit === undefined || resultingCount <= limit;
}
/** Type guard: is `value` one of the known tier ids? */
function isTierId(value) {
    return typeof value === 'string' && exports.TIER_IDS.includes(value);
}
/** Type guard: is `value` one of the known feature keys? */
function isFeatureKey(value) {
    return typeof value === 'string' && exports.FEATURE_KEYS.includes(value);
}
/**
 * Lifecycle of a tenant (ARCH-002, extended by ARCH-006/PRD-551): a fresh
 * self-registered tenant is `trialing`; activation (staff or a paid first
 * payment) makes it `active`; past `trialEndsAt` without activation, or after a
 * lapsed grace period, it is `expired` (soft read-only). A paying tenant whose
 * charge failed enters `past_due` — writable during a grace window
 * (`graceEndsAt`) while the charge is retried, then `expired`.
 */
exports.TRIAL_STATUSES = ['trialing', 'active', 'expired', 'past_due'];
/** Type guard: is `value` one of the trial lifecycle states? */
function isTrialStatus(value) {
    return typeof value === 'string' && exports.TRIAL_STATUSES.includes(value);
}
/**
 * The entitlement actually in force. Since PLN-1807 Amendment 2 the trial no
 * longer unlocks any richer feature set — a tenant always runs on its CHOSEN
 * tier's features, in every lifecycle state. The trial's only remaining effect
 * is on WRITABILITY (a lapsed paid trial goes read-only via `trialStatus` /
 * `tenantWritable`), never on the feature set. Kept as the single persist-path
 * for the stored `features` map (registration, tier change, seed, reconcile);
 * `status` is accepted for that call-site stability but no longer changes the
 * result.
 */
function effectiveFeatures(status, targetTier, overrides = {}, installedApps = []) {
    void status;
    return resolveFeatures(targetTier, overrides, installedApps);
}
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * The writable branch every writability layer carries for a free tenant's
 * in-app trial: `status == 'trialing' && trialFallbackTier == 'free'`. The
 * `tenantWritable()` rules helper and `requireWritableTenant` spell the same
 * condition; this is its one TypeScript form.
 */
function isFreeOriginTrial(snapshot) {
    return snapshot.status === 'trialing' && snapshot.trialFallbackTier === 'free';
}
/**
 * Derive the effective trial state at `now` (injected for determinism). The
 * rules gate is time-based and self-enforcing, so a stored `trialing` past
 * its `trialEndsAt` is reported as `expired` here without any scheduled job —
 * the same predicate `firestore.rules` (`tenantWritable()`) and the callable
 * guard (`requireWritableTenant`) enforce. Profiles from before the trial era
 * (no valid `status`) carry no trial and stay writable.
 */
function trialStatus(snapshot, now) {
    if (!isTrialStatus(snapshot.status)) {
        return { status: null, daysLeft: 0, isReadOnly: false };
    }
    if (snapshot.status === 'active') {
        return { status: 'active', daysLeft: 0, isReadOnly: false };
    }
    // A paying tenant whose charge failed is judged by its grace window, never by
    // a trial end it does not carry — the same branch `tenantWritable()` has.
    if (snapshot.status === 'past_due') {
        const graceEndsAt = snapshot.graceEndsAt;
        return graceEndsAt instanceof Date && now.getTime() < graceEndsAt.getTime()
            ? { status: 'past_due', daysLeft: 0, isReadOnly: false }
            : { status: 'expired', daysLeft: 0, isReadOnly: true };
    }
    const endsAt = snapshot.trialEndsAt;
    const running = endsAt instanceof Date && now.getTime() < endsAt.getTime();
    // A free tenant's in-app trial lands on `free`, which is writable, so its
    // lapse is never a read-only state — the scheduled fallback just has not run
    // yet. Keyed on `trialing` too, so a stale marker cannot open an expired
    // tenant (ARCH-002 issue-#1106 amendment point 5).
    if (!running && isFreeOriginTrial(snapshot)) {
        return { status: 'trialing', daysLeft: 0, isReadOnly: false };
    }
    if (!running) {
        return { status: 'expired', daysLeft: 0, isReadOnly: true };
    }
    return {
        status: snapshot.status,
        daysLeft: snapshot.status === 'trialing' ? Math.ceil((endsAt.getTime() - now.getTime()) / DAY_MS) : 0,
        isReadOnly: false,
    };
}
/**
 * Trial length: 30 days, uniformly for every tier and tenant, per PRD-1801 /
 * ARCH-002 (issue #732 — two weeks proved too short to evaluate the CRM in a
 * real working month). The single source of truth: `registerTenant` and the
 * seed both derive their windows from it, so there is no second place to keep
 * in sync.
 */
exports.TRIAL_DAYS = 30;
/** Start a trial at `now` (injected for determinism). */
function computeTrial(now) {
    const trialEndsAt = new Date(now.getTime() + exports.TRIAL_DAYS * 24 * 60 * 60 * 1000);
    return { trialStartedAt: now, trialEndsAt, status: 'trialing' };
}
/**
 * Timetracking role↔capability coupling (ARCH-001 amendment, PRD-1807): the
 * `timetracking` user role may only be assigned while the tenant's EFFECTIVE
 * features include the `timetracking` capability — effective (not target-tier)
 * so a trialing tenant can evaluate timetracking users.
 */
function canAssignTimetrackingRole(effective) {
    return effective.timetracking === true;
}
/**
 * Whether a tenant can hold mobile seats — included or bought. A mobile seat is
 * occupied only by the `timetracking` role, so it is holdable exactly while that
 * role is assignable: the same EFFECTIVE features (tier < apps < overrides),
 * never the tier alone. Every path that sells or keeps a mobile seat asks this —
 * no tenant pays for a seat class it cannot occupy (PRD-1807 Amendment 5).
 */
function canHoldMobileSeats(effective) {
    return canAssignTimetrackingRole(effective);
}
/**
 * The users blocking a tier change: a self-service change whose RESULTING
 * effective features lack `timetracking` must be refused while
 * timetracking-role users exist (no silent auto-demotion — the owner re-roles
 * them deliberately). Returns the blocking user ids; empty means proceed.
 */
function timetrackingChangeBlockers(nextEffective, timetrackingUserIds) {
    return nextEffective.timetracking === true ? [] : [...timetrackingUserIds];
}
/**
 * Resolve the effective feature map for a tenant from three ordered sources
 * (PRD-816, ARCH-002 app-axis amendment):
 *
 * ```
 * tier defaults  <  installed apps  <  platform overrides
 * ```
 *
 * A bookable app grants exactly what a tier grants — capability keys in the
 * resolved map — so every enforcement layer the product already has (the
 * `hasFeature()` rules clauses, the route `featureGuard`s, the callable-side
 * `requireFeature`) enforces a purchase without a single new check. The store
 * adds a way to ACQUIRE capabilities and no second way to enforce them.
 *
 * A platform override stays the last word: it is the instrument for comped and
 * negotiated arrangements, and must be able to withdraw a capability an app
 * grants as much as to add one. Unknown override keys are ignored so a typo can
 * never silently unlock or remove a feature; unknown app ids grant nothing
 * (see {@link featuresFromApps}). Returns a fresh object — the `TIERS` defaults
 * are never mutated.
 */
function resolveFeatures(tier, overrides = {}, installedApps = []) {
    const resolved = { ...exports.TIERS[tier] };
    for (const key of (0, app_catalog_js_1.featuresFromApps)(installedApps)) {
        resolved[key] = true;
    }
    for (const key of exports.FEATURE_KEYS) {
        const override = overrides[key];
        if (typeof override === 'boolean') {
            resolved[key] = override;
        }
    }
    return resolved;
}
/**
 * Reconstruct the override set that would reproduce `features` when applied
 * on top of the tenant's EFFECTIVE BASE via {@link resolveFeatures}: the tier
 * defaults plus whatever its installed apps grant. Diffing against the tier
 * alone would make an app-granted capability indistinguishable from a manual
 * grant — the next tier change would merge it forward into `overrides`, and a
 * later uninstall would leave the capability standing while the charge stopped
 * (PRD-816, ARCH-002 override boundary). A purchase is never persisted or
 * displayed as an override. The inverse can only recover
 * the DELTA, not original caller intent — an override that happens to match
 * the tier default is indistinguishable from no override at all once
 * persisted as a flat map (PLN-1823 Phase 2, closes the `setTenantTier`
 * override-wipe gap: `getPlatformTenant` seeds the UI's override checkboxes
 * from this, and `setTenantTier` merges it forward for any key absent from a
 * request).
 */
function diffFeatureOverrides(tier, features, installedApps = []) {
    const defaults = resolveFeatures(tier, {}, installedApps);
    const overrides = {};
    for (const key of exports.FEATURE_KEYS) {
        if (features[key] !== defaults[key]) {
            overrides[key] = features[key];
        }
    }
    return overrides;
}
//# sourceMappingURL=entitlements.js.map